import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StateService } from '@app/service/state/state.service';
import { HeaderService } from '@app/service/header/header.service';
import {
  CreditService,
  FinsharkResult,
} from '@app/service/credit/credit.service';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { routeNames } from '@assets/val/route-constants';
import { CustomerJourneyService } from '@app/service/customer-journey/customer-journey.service';
import { PreventBackToSettlementOrAuthGuard } from '@app/guard/prevent-back-to-settlement-or-auth-guard.service';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  Data,
  EventResult,
} from '@app/component/finshark-iframe/finshark-iframe.component';
import {
  PaymentMethod,
  PaymentRequestDto,
} from '@app/model/payment-request/payment-request.dto';
import { SelectorItem } from '@app/component/selector/selector.component';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { IframeService } from '@app/service/iframe/iframe.service';

@Component({
  selector: 'app-finshark',
  templateUrl: './finshark.component.html',
  styleUrls: ['../page-shared.css', './finshark.component.scss'],
})
export class FinsharkComponent implements OnInit {
  paymentRequest: PaymentRequestDto;
  paymentMethod: string;
  amount: number;
  modalActive: boolean = false;
  selectorItems: SelectorItem[] = null;

  loading: boolean = true;
  error: boolean = false;
  hasFinsharkError: boolean = false;
  navigationInProgress: boolean = false;
  shouldDisplayPreFinshark: boolean = false;
  selectedMethodId: string;
  shouldRequireFutherAIS: boolean;
  isIframe: boolean;

  iframeUrl: SafeResourceUrl;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,

    private creditService: CreditService,
    private queryParamsService: QueryParamsService,
    private customerJourneyService: CustomerJourneyService,
    private preventBackToSettlementOrAuthGuard: PreventBackToSettlementOrAuthGuard,
    private loggingService: LoggingService,
    private sanitizer: DomSanitizer,
    private valuePipe: ValuePipe,
    private iframeService: IframeService
  ) {}

  ngOnInit(): void {
    this.isIframe = this.iframeService.isThisInIframe();

    const queryParams = this.queryParamsService.getQueryParams();
    this.selectedMethodId = queryParams.method;

    if (
      queryParams.authorizationId &&
      queryParams.sessionId &&
      queryParams.result === 'success' &&
      queryParams.verifiedAccountId
    ) {
      // If we have query params from finshark, we can skip directly to handling the results.
      this.handleFinsharkResults({
        authorizationId: queryParams.authorizationId,
        sessionId: queryParams.sessionId,
        result: EventResult.SUCCESS,
        verifiedAccountId: queryParams.verifiedAccountId,
      } as Data);
    } else {
      this.setup();
    }
  }

  async setup(): Promise<void> {
    await this.getIsFurtherAISRequired();
    await this.handleAISRequiredResponse();
  }

  async getIsFurtherAISRequired(): Promise<void> {
    try {
      if (this.selectedMethodId) {
        this.shouldRequireFutherAIS = await this.creditService
          .getIfFurtherAISIsRequired(this.selectedMethodId)
          .toPromise();
      } else {
        this.loggingService.error(
          'SelectedMethodId is required in call to determine if Ais is needed, but it was missing.'
        );
        this.error = true;
      }
    } catch (err) {
      this.loggingService.error(
        'Call to backend failed, unknown if additional AIS should be done.'
      );
      this.error = true;
    }
  }

  async handleAISRequiredResponse(): Promise<void> {
    if (this.shouldRequireFutherAIS) {
      const paymentRequestId: string =
        this.queryParamsService.getQueryParams().id;

      this.creditService
        .getPaymentRequestPublicInfo(paymentRequestId)
        .subscribe((res) => {
          this.paymentRequest = res;
          this.paymentMethod = this.getPaymentMethod();
          this.amount = this.paymentRequest.amount;
          this.setModalInfo();
        });
    } else {
      this.navigateToEmail();
    }

    this.loading = false;
  }

  async fetchFinsharkUrl(): Promise<void> {
    this.loading = true;

    //URL Params to add to route back from Finshark.
    const redirectUrlParams = this.objectToQueryString(
      this.queryParamsService.getQueryParams()
    );

    try {
      await this.creditService
        .postFinsharkUrl(redirectUrlParams, true)
        .then((resp) => {
          if (!resp || !resp.url) {
            this.error = true;
          } else {
            this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              resp.url
            );
          }
        });
    } catch (err) {
      this.loggingService.error(
        `Finshark URL could not be created due to: ${JSON.stringify(err)}`
      );
      this.error = true;
    }

    this.loading = false;
  }

  handleCtaClick(): void {
    this.navigateTo(routeNames.HOME);
  }

  async handleContinueClick(): Promise<void> {
    await this.fetchFinsharkUrl();
  }

  async handleAttemptFinsharkRedirect(): Promise<void> {
    this.loading = true;
    this.hasFinsharkError = false;

    //URL Params to add to route back from Finshark.
    const redirectUrlParams = this.objectToQueryString(
      this.queryParamsService.getQueryParams()
    );

    try {
      await this.creditService
        .postFinsharkUrl(redirectUrlParams, false)
        .then((resp) => {
          if (!resp || !resp.url) {
            this.error = true;
          } else {
            window.location.href = resp.url;
          }
        });
    } catch (err) {
      this.loggingService.error(
        `Finshark redirect URL could not be created due to: ${JSON.stringify(
          err
        )}`
      );
      this.error = true;
    } finally {
      // Wait two seconds, if the redirect was successful
      // the user will no longer be on the page so it won't
      // set loading to false unnecessarily.
      setTimeout(() => {
        this.loading = false;
      }, 2000);
    }
  }

  async onEventData(eventData: Data): Promise<void> {
    if (eventData.result === EventResult.SUCCESS) {
      await this.handleFinsharkResults(eventData);
    } else if (eventData.result === EventResult.ERROR) {
      this.loggingService.error(
        `Finshark failed and responded with: ${JSON.stringify(
          eventData
        )}, displaying error with fallback redirect option...`
      );
      this.hasFinsharkError = true;
      this.error = true;
    } else if (eventData.result === EventResult.CANCELED) {
      await this.navigateTo(routeNames.HOME);
    }
  }

  async handleFinsharkResults(eventData: Data): Promise<void> {
    const finsharkResult: FinsharkResult = {
      authorizationId: eventData.authorizationId,
      sessionId: eventData.sessionId,
      result: eventData.result,
      verifiedAccountId: eventData.verifiedAccountId,
      ownerName: '',
    };

    if (
      !finsharkResult.authorizationId ||
      !finsharkResult.verifiedAccountId ||
      !finsharkResult.sessionId ||
      finsharkResult.result !== EventResult.SUCCESS
    ) {
      this.loggingService.error(
        `Finshark was successful but responded with: ${JSON.stringify(
          finsharkResult
        )}`
      );
      this.error = true;
      return;
    }

    try {
      await this.creditService
        .postFinsharkResults(finsharkResult, this.selectedMethodId)
        .then(() => this.navigateToEmail());
    } catch (err) {
      this.loggingService.error(
        `Failed to post finshark results to backend, due to: ${JSON.stringify(
          err
        )}`
      );
      this.error = true;
    }
  }

  navigateTo(routeName: string): void {
    this.navigationInProgress = true;

    this.router.navigate([routeName], {
      queryParamsHandling: 'merge',
    });

    this.navigationInProgress = false;
  }

  navigateToEmail(): void {
    this.navigationInProgress = true;
    this.preventBackToSettlementOrAuthGuard.deactivate();

    this.customerJourneyService.getNextRoute().subscribe((route) => {
      this.router
        .navigate([route], {
          queryParamsHandling: 'merge',
          queryParams: {
            // Clears the finshark query params as they are no longer needed.
            authorizationId: null,
            result: null,
            verifiedAccountId: null,
            sessionId: null,
          },
        })
        .then(
          () => {
            this.navigationInProgress = false;
            this.preventBackToSettlementOrAuthGuard.activate();
          },
          (rejection) => {
            this.loggingService.error(
              `Navigation from ${routeNames.FINSHARK} to ${route} was rejected.`,
              rejection
            );
          }
        );
    });
  }

  objectToQueryString(obj: Record<string, any>): string {
    return Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
      )
      .join('&');
  }

  getPaymentMethod(): string {
    const paymentMethod: string =
      this.queryParamsService.getQueryParams().methodType;

    if (
      paymentMethod === PaymentMethod.CREDIT ||
      paymentMethod === PaymentMethod.CREDIT_STATIC_AMOUNT
    ) {
      return 'Delbetalning';
    }

    return 'Faktura';
  }

  setModalInfo(): void {
    const lineItems: boolean = this.paymentRequest.lineItems.length > 0;
    const totalValue: string = lineItems
      ? 'Totalbelopp inkl. moms:'
      : 'Totalbelopp:';
    this.selectorItems = [
      {
        tab: 'Ditt köp',
        title: lineItems ? 'Sammanfattning' : this.paymentRequest.title,
        lineItems: lineItems ? this.paymentRequest.lineItems : null,
        descriptions: [{ text: this.paymentRequest.description }],
        totalValue: `${totalValue} ${this.valuePipe.transform(
          this.paymentRequest.value
        )}`,
      },
    ];
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }
}
